import React from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import { translator } from 'mage-i18n';
import {
  analytics,
  defaultContainerPropTypes,
  formUtils,
  HorizontalWrapper,
  payloadUtils,
  Input,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import { CAPS } from '@/constants';
import { getMerchantCountryKey, getMerchantAwareTranslator } from '@/selectors/merchant';
import {
  BackButton,
  CallToActionButton,
  ContainerTemplate,
  FieldSetWithAddressForm,
} from '@/components';
import { withCapabilityBeforeNext } from '@/flow/common';

import { CY_PARTNERSHIP_DETAILS_CONTAINER_IDS } from './constants';
import { getDefaultState } from './selectors';
import { ANALYTICS_EVENTS } from './analyticsEvents';

const tPrefix = 'products-fe.containers.partnership_details';

export class PartnershipDetails extends React.Component {
  constructor(props) {
    super(props);

    this.prepareFields = this.prepareFields.bind(this);
    this.onChangeFields = this.onChangeFields.bind(this);
    this.onNext = this.onNext.bind(this);
    this.onInit = this.onInit.bind(this);

    const fields = this.prepareFields(props);
    const initialState = { fields };

    this.state = {
      ...initialState,
      initialState,
    };
  }

  prepareFields({ t, partnershipDetails = {}, merchantCountry }) {
    const {
      operatingAddress: {
        country = merchantCountry,
        postalCode = '',
        street = '',
        city = '',
        region = '',
      } = {},
    } = partnershipDetails;
    return {
      companyName: {
        component: Input,
        size: 1,
        type: 'text',
        label: t(`${tPrefix}.fields.partnership_name.label`),
        validators: [
          {
            callback: validators.common.isRequired,
            errorMessage: t(`${tPrefix}.fields.partnership_name.errors.general`),
          },
        ],
        extraProps: {
          autoFocus: true,
          ...generateCyElemId(CY_PARTNERSHIP_DETAILS_CONTAINER_IDS.INPUT_PARTNERSHIP_NAME, window),
        },
        isInitialized: false,
        isValid: false,
        value: partnershipDetails.companyName,
      },
      country: { value: country, extraProps: { disabled: true } },
      postalCode: { value: postalCode },
      street: { value: street },
      city: { value: city },
      region: { value: region },
    };
  }

  onChangeFields(key, val, fields) {
    formUtils.onChangeFields(key, val, { fields }, fields => {
      this.setState({ fields });
    });
  }

  onInit(fields) {
    const initializedFields = formUtils.initForm(fields);
    this.setState({ fields: initializedFields });
  }

  getBody({ fields }) {
    const { companyName, ...operatingAddress } = formUtils.getMappedValues(fields);
    return {
      companyName,
      operatingAddress,
    };
  }

  onNext() {
    this.setState({
      ...this.state,
      fields: formUtils.initializeFields(this.state.fields),
    });

    formUtils.callToActionOnNextWrapper(this.state.fields, () => {
      this.props.onNext(
        payloadUtils.getMerchantCapabilityPayload(this.props.mid, this.state, this.getBody),
      );
    });
  }

  render() {
    const { error, hasBack, isDisabled, isLoading, onBack, t, tMerchantAware } = this.props;
    const { fields } = this.state;
    const tabIndex = Object.keys(fields).length + 5;

    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical small />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <FieldSetWithAddressForm
            isBorderless
            isDisabled={isDisabled}
            name="partnership-details"
            fields={fields}
            onChange={this.onChangeFields}
            onInit={this.onInit}
          />
        </div>
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton tabIndex={tabIndex + 1} onClick={onBack} disabled={isDisabled}>
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            tabIndex={tabIndex}
            loading={isLoading}
            onClick={this.onNext}
            disabled={isDisabled}
            {...generateCyElemId(CY_PARTNERSHIP_DETAILS_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

PartnershipDetails.propTypes = {
  ...defaultContainerPropTypes,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  merchantCountry: PropTypes.string,
  partnershipDetails: PropTypes.shape({
    companyName: PropTypes.string,
    operatingAddress: PropTypes.shape({
      city: PropTypes.string,
      country: PropTypes.string,
      postalCode: PropTypes.string,
      street: PropTypes.string,
    }),
  }),
};

export default compose(
  connect(state => ({
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
    merchantCountry: getMerchantCountryKey(state),
    partnershipDetails: getDefaultState(state),
  })),
  withCapabilityBeforeNext({ patch: CAPS.V2_MERCHANTS_BUSINESS_DETAILS_PATCH }, ({ payload }) =>
    analytics.logEvent(ANALYTICS_EVENTS.submitPartnershipDetails, payload.body),
  ),
)(PartnershipDetails);
